import { create } from 'zustand';
import { CancelTokenSource } from 'axios';
import ResponseError from '../../types/response/ResponseError';
import { PagingResponseData } from '../../types/response/PagingResponseData';
import CheckoutSession from '../../types/paylink/CheckoutSession';
import { PaylinkFormRow } from '../../types/paylink/PaylinkFormRow';
import { OrderAddress } from '../../types/OrderAddress';
import { ElectronicInvoiceAddress } from '../../types/order/ElectronicInvoiceAddress';

export interface PrefilledPurchase {
    storeId?: string;
    registrationNumber?: string;
    countryCode?: string;
    email?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    costCenter?: string;
    orderReference?: string;
    invoiceReference?: string;
    deliveryAddress?: OrderAddress;
    invoiceAddress?: OrderAddress;
    electronicInvoiceAddress?: ElectronicInvoiceAddress;
    articles: PaylinkFormRow[];
}

export interface PaylinksStore {
    isSearching: boolean;
    searchResult: PagingResponseData<CheckoutSession[]>;
    isFailed: boolean;
    error: ResponseError;
    requestParameters: string | undefined;
    searchCancelToken: CancelTokenSource;
    isCanceled: boolean;
    exactSearch: boolean;
    showCreatePaylinkModal: boolean;
    showCreateServicePurchaseModal: boolean;
    showCreateFraudPurchaseModal: boolean;
    showCreateAdvanceInvoiceModal: boolean;
    prefilledFromExistingPurchase?: PrefilledPurchase;
    phoneNumber: string;
    searchStarted: (requestParameters: string, cancelToken: CancelTokenSource) => void;
    searchFailed: (error: ResponseError) => void;
    searchSucceeded: (data: PagingResponseData<CheckoutSession[]>) => void;
    searchCanceled: () => void;
    setExactSearch: (enabled: boolean) => void;
    setCreatePaylinkModal: (open: boolean) => void;
    setCreateServicePurchaseModal: (open: boolean) => void;
    setCreateFraudPurchaseModal: (open: boolean) => void;
    setCreateAdvanceInvoiceModal: (open: boolean) => void;
    setPrefilledFromExistingPurchase: (purchase: PrefilledPurchase | undefined) => void;
    setPhoneNumber: (phoneNumber: string) => void;
    reset: () => void;
}

export const initialState = {
    isSearching: false,
    searchResult: {} as PagingResponseData<CheckoutSession[]>,
    isFailed: false,
    error: {} as ResponseError,
    requestParameters: '',
    searchCancelToken: {} as CancelTokenSource,
    isCanceled: false,
    exactSearch: false,
    showCreatePaylinkModal: false,
    showCreateServicePurchaseModal: false,
    showCreateFraudPurchaseModal: false,
    showCreateAdvanceInvoiceModal: false,
    prefilledFromExistingPurchase: undefined,
    phoneNumber: '',
};

export const usePaylinksStore = create<PaylinksStore>(set => ({
    ...initialState,

    searchStarted: (requestParameters: string, cancelToken: CancelTokenSource) =>
        set({
            isSearching: true,
            isCanceled: false,
            requestParameters: requestParameters,
            searchCancelToken: cancelToken,
        }),

    searchFailed: (error: ResponseError) =>
        set({
            error: error,
            isSearching: false,
            isCanceled: false,
            isFailed: true,
            searchResult: {} as PagingResponseData<CheckoutSession[]>,
        }),

    searchSucceeded: (data: PagingResponseData<CheckoutSession[]>) =>
        set({
            searchResult: data,
            isCanceled: false,
            isSearching: false,
            isFailed: false,
        }),

    searchCanceled: () =>
        set({
            isCanceled: true,
        }),

    setExactSearch: (enabled: boolean) =>
        set({
            exactSearch: enabled,
        }),

    setCreatePaylinkModal: (open: boolean) =>
        set({
            showCreatePaylinkModal: open,
        }),

    setCreateServicePurchaseModal: (open: boolean) =>
        set({
            showCreateServicePurchaseModal: open,
        }),

    setCreateFraudPurchaseModal: (open: boolean) =>
        set({
            showCreateFraudPurchaseModal: open,
        }),

    setCreateAdvanceInvoiceModal: (open: boolean) =>
        set({
            showCreateAdvanceInvoiceModal: open,
        }),

    setPrefilledFromExistingPurchase: (purchase: PrefilledPurchase | undefined) =>
        set({
            prefilledFromExistingPurchase: purchase,
        }),

    setPhoneNumber: (phoneNumber: string) =>
        set({
            phoneNumber: phoneNumber,
        }),

    reset: () => set(initialState),
}));
